import React, { forwardRef } from 'react';
import styled from 'styled-components';
import * as BuilderUI from '../BuilderUI';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { PermissionKey } from '../../../utils/apollo/graphqlSchema';
import { useAppSelector } from '../../../redux/hooks';
import { activeSchemaIdSelector } from '../../../redux/selectors/builder';
import { theme } from '../../../styles/theme';
import PermissionElementWrapper from '../../../pages/PermissionControls/PermissionElementWrapper';
import { SingleSelect, SingleSelectOption } from '../../Dropdown/SingleSelect';

interface IProps {
  onAddGlobalFilter?: () => void;
  onAddCohortFilter?: () => void;
  onAddMeasureFilter?: () => void;
  disableCohortFilter: boolean;
  disableMeasureFilter: boolean;
}

const Label = styled(BuilderUI.LabelAction)`
  &:hover,
  [aria-expanded='true'] & {
    color: ${theme.colors.mainBlue};
    cursor: pointer;
  }
`;

const ReportFilterMenu = forwardRef<HTMLDivElement, IProps>(
  (
    {
      onAddGlobalFilter,
      onAddCohortFilter,
      onAddMeasureFilter,
      disableCohortFilter,
      disableMeasureFilter,
    },
    ref,
  ) => {
    const { checkIsPermitted } = usePermission();
    const activeSchemaId = useAppSelector(activeSchemaIdSelector);
    const isPermittedToReadField = checkIsPermitted({
      requiredPermissionKeys: [PermissionKey.FieldRead],
      schemaId: activeSchemaId,
    });

    return (
      <SingleSelect
        ref={ref}
        trigger={<Label>+ Add Filter</Label>}
        placement="bottom-start"
      >
        {onAddGlobalFilter && (
          <PermissionElementWrapper isPermitted={isPermittedToReadField}>
            <SingleSelectOption
              iconName="global-filter"
              label={'Global Filter'}
              onClick={onAddGlobalFilter}
              disabled={!isPermittedToReadField}
              tooltipOption={{
                content:
                  'Apply a global filter that is applied to all chart criteria.',
              }}
              tooltipPlacement="right"
            />
          </PermissionElementWrapper>
        )}
        {onAddCohortFilter && (
          <SingleSelectOption
            iconName="cohort-filter"
            label={'Cohort Filter'}
            onClick={onAddCohortFilter}
            disabled={disableCohortFilter}
            tooltipOption={{
              content:
                'Apply a global cohort group for a more granular analysis.',
            }}
            tooltipPlacement="right"
          />
        )}
        {onAddMeasureFilter && (
          <SingleSelectOption
            iconName="measure-filter"
            label={'Measure Filter'}
            onClick={onAddMeasureFilter}
            disabled={disableMeasureFilter}
            tooltipOption={{
              content: 'Add Measure Filter',
            }}
            tooltipPlacement="right"
          />
        )}
      </SingleSelect>
    );
  },
);

export default ReportFilterMenu;
